<template>
  <div ref="rightSide" class="space-y-5 md:space-y-6">
    <div class="flex items-start justify-between gap-2.5">
      <h1 class="text-xl font-light !tracking-[0.2px] text-gray-100" data-e2e-item="product-title">
        {{ productTitle }}
      </h1>
      <ClientOnly>
        <UiButton
          v-if="
            currentConfiguration?.data &&
            currentConfiguration.data.configuration_id
          "
          class="add-to-bag-section-icon hidden flex-shrink-0 lg:inline-flex"
          variant="clear"
          variant-type="icon"
          size="xs"
          :aria-label="$t('addToWishlist')"
          data-e2e-btn="add-to-wishlist"
          @click="onUpdateWishlist"
        >
          <UiIcon
            name="heart-bold"
            class="size-3.5"
            :class="[inWishlist ? 'fill-primary text-primary' : '']"
            aria-hidden="true"
          />
        </UiButton>
      </ClientOnly>
    </div>

    <div v-if="currentConfiguration?.data">
      <div v-if="isOutOfStock" class="mb-1.5 text-xl font-light text-orange-60">
        {{ $t("outOfStock") }}
      </div>
      <div v-else class="mb-1.5 flex gap-1 text-xl font-light text-orange-60">
        <span v-if="startAtPrice" class="text-gray-70 line-through">
          {{ $n(startAtPrice, "currency") }}
        </span>
        {{ $n(finalProductPrice, "currency") }}
      </div>

      <ProductAffirmBlock v-show="!isOutOfStock && !productInfo.digital" />
    </div>

    <div
      v-if="filtersPdpManyInOne"
      class="border-t border-off-white-30 max-md:-mx-4 max-md:px-4"
    >
      <div v-for="block in filteredMaterialsByCategories" :key="block?.id">
        <ProductMaterialFilter
          :result="result"
          :additional-options="additionalOptions"
          :block="block"
          is-regular-pdp
          @filter="onFilterClick"
          @options-filter="onOptionsFilterClick"
        />
      </div>
    </div>

    <section
      v-if="!isOutOfStock"
      class="add-to-bag-section relative"
    >
      <div class="add-to-bag-btn">
        <UiButton
          :loading="addToCartLoading"
          class="w-full uppercase"
          size="lg"
          :disabled="addToCartLoading || hasUnavailableMaterial"
          @click="onAddToCart"
        >
          {{ $t("addToCart") }}
          <UiIcon name="bag" class="ml-2 size-6" />
        </UiButton>
      </div>
      <SharedFieldHint
        v-if="hasUnavailableMaterial"
        class="mt-4"
        :message="$t('unavailableConfiguration')"
        error
      />
    </section>

    <ClientOnly>
      <UiButton
        v-if="
          currentConfiguration?.data &&
          currentConfiguration.data.configuration_id
        "
        color="primary"
        variant="outlined"
        size="lg"
        class="w-full uppercase"
        :aria-label="$t('addToWishlist')"
        data-e2e-btn="add-to-wishlist"
        @click="onUpdateWishlist"
      >
        <UiIcon
          name="heart-bold"
          class="size-5 mr-2"
          :class="[inWishlist ? 'fill-primary text-primary' : '']"
          aria-hidden="true"
        />
        <span class="mt-0.5">
          {{ $t("addToWishlist") }}
        </span>
      </UiButton>
    </ClientOnly>

    <PagesProductDeliveryInfo
      :show-free-resize="showFreeResize"
      :ship-date="currentConfiguration?.data?.ship_date"
      :product-collections="productCollections"
      :only-return="onlyReturn"
      :show-delivery-info-truck="showDeliveryInfoTruck"
      :current-configuration="currentConfiguration"
      :product-info="productInfo"
      :lead-time="leadTime"
    />

    <ProductFinalSalePDP
      v-if="trackInventory && !isReadyToShipCollection"
      class="!mt-2.5"
    />

    <div class="max-md:-mx-4">
      <ProductExpandedBlock :title="$t('productDetails')">
        <ProductDetailsInfo
          :details-map="detailsMapedData"
          :description="productDescription"
        />
      </ProductExpandedBlock>

      <ProductExpandedBlock
        v-if="shippingReturnsAccordionData && !productInfo.digital"
        :title="shippingReturnsAccordionData.title"
        :content="shippingReturnsAccordionData.content"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { IProductPdpInfoProps, MaterialData, ProductData } from "ecom-types";
import ProductMaterialFilter from "~/components/product/ProductMaterialFilter.vue";
import {
  ALLOWED_FREE_RESIZE_CATEGORY_SKUS,
  COLLECTION_NO_FREE_RESIZE_SLUGS,
  EXCLUDED_FREE_RESIZE_CATEGORY_SKUS,
} from "~/configs";

const props = defineProps<
  IProductPdpInfoProps & {
    productInfo: ProductData;
    leadTime: number;
  }
>();

const emit = defineEmits([
  "addToCart",
  "optionsFilter",
  "filter",
  "updateWishlist",
]);

const onlyReturn = computed(() => {
  if (Array.isArray(props.productCollections)) {
    return props.productCollections.some((collection) =>
      COLLECTION_NO_FREE_RESIZE_SLUGS.includes(collection.slug),
    );
  }
  return false;
});

const showFreeResize = computed(() => {
  if (Array.isArray(props.productCategories)) {
    const hasExcludedCategory = props.productCategories.some((category) =>
      EXCLUDED_FREE_RESIZE_CATEGORY_SKUS.includes(category.sku),
    );

    if (hasExcludedCategory) {
      return false;
    }

    return props.productCategories.some((category) =>
      ALLOWED_FREE_RESIZE_CATEGORY_SKUS.includes(category.sku),
    );
  }
  return false;
});

// TODO Remove hardcode when collection is not going to be used
const isReadyToShipCollection = computed(() => {
  return props.productCollections?.some(
    (collection) => collection.slug === "ready-to-ship-collection",
  );
});

const showDeliveryInfoTruck = computed(() => {
  return !props.isOutOfStock && !props.productInfo.digital;
});

function onAddToCart() {
  if (props.hasUnavailableMaterial) return;
  emit("addToCart");
}

function onOptionsFilterClick(value: { slug: string; item?: MaterialData }) {
  emit("optionsFilter", value);
}

function onFilterClick(value: { slug: string; item?: MaterialData }) {
  emit("filter", value);
}

function onUpdateWishlist() {
  emit("updateWishlist");
}
</script>
